import React, {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  type TextareaHTMLAttributes,
  useEffect,
  useState,
} from 'react';

import {
  Controller,
  type FieldValues,
  type Path,
  get,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import TextareaAutosize from '@mui/material/TextareaAutosize';
import { twMerge } from 'tailwind-merge';
import { ErrorMessage } from 'ui/atoms';

import { CustomTooltipProps } from '../../../molecules/Tooltip/Tooltip';
import { HelpIconWithTooltip } from '../../HelpIconWithTooltip/HelpIconWithTooltip';

interface _TextAreaProps {
  name?: string;
  className?: string;
  textareaClassName?: string;
  isFocused?: boolean;
  setIsFocused?: Dispatch<SetStateAction<boolean>>;
  isError?: boolean;
  errorMessage?: string;
  register?: (name: string) => any;
  showCharacterCount?: boolean;
  icon?: ReactNode;
  hasIconDivider?: boolean;
  autoResize?: boolean;
  maxRows?: number;
  minRows?: number;
  helpTooltip?: string;
  helpToolTipPlacement?: CustomTooltipProps['placement'];
}

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & _TextAreaProps;

export const TextAreaBase = ({
  isFocused,
  isError,
  className,
  ...restProps
}: {
  isFocused?: boolean;
  isError?: boolean;
  className?: string;
} & TextareaHTMLAttributes<HTMLTextAreaElement>) => {
  return (
    <textarea
      data-testid="outvioui--textarea"
      className={twMerge(
        'w-full py-[11px] outline-none-[none] px-[16px] rounded-[10px] outline-0 bg-v2blueGray-0 placeholder:font-inter font-inter text-[14px] border text-v2blueGray-800 leading-[22px] resize-none',
        isFocused && '!border-v2blueGray-200',
        isError ? `border-v2red placeholder-v2red` : `border-v2blueGray-0`,
        className,
      )}
      {...restProps}
    />
  );
};

export function TextArea({
  name = '',
  className,
  onBlur,
  textareaClassName,
  isFocused,
  setIsFocused,
  isError,
  errorMessage,
  register,
  showCharacterCount,
  maxLength,
  icon,
  hasIconDivider = false,
  autoResize,
  maxRows,
  minRows,
  helpTooltip,
  helpToolTipPlacement = 'top',
  ...otherProps
}: TextAreaProps) {
  let onBlurRHF: (event: React.FocusEvent<HTMLTextAreaElement, Element>) => void;
  let onChangeRHF: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  let restRHF = {};
  if (register) {
    const { onBlur, onChange, ...rest } = register(name);
    onBlurRHF = onBlur;
    restRHF = rest;
    onChangeRHF = onChange;
  }

  const Tag = autoResize ? TextareaAutosize : 'textarea';

  const additionalProps = autoResize
    ? {
        minRows,
        maxRows,
      }
    : {};

  return (
    <div className={twMerge('flex flex-col flex-1', className)}>
      <div className="relative flex-1 flex flex-col ">
        {icon && (
          <>
            <div
              className={twMerge(
                'pointer-events-none absolute z-1 top-[6px] left-[10px] flex items-center justify-center h-6 w-6 text-[16px] text-v2blueGray-500',
              )}
            >
              {icon}
            </div>
            {hasIconDivider && (
              <div
                className={twMerge('w-px bg-v2blueGray-200 h-5 absolute top-[8px]', 'left-[42px]')}
              />
            )}
          </>
        )}
        <Tag
          data-testid="outvioui--textarea"
          className={twMerge(
            'w-full hover:bg-v2blueGray-100 py-[6px] focus:outline-none outline-none-[none] px-[16px] rounded-[10px] outline-0 bg-v2blueGray-0 placeholder:font-inter font-inter text-[14px] border text-v2blueGray-800 leading-[22px] resize-none',
            isFocused && '!border-v2blueGray-200',
            isError ? `border-v2red` : `border-v2blueGray-0`,
            icon && 'pl-[50px]',
            !(!!autoResize || !!otherProps.rows) && 'min-h-[88px]',
            textareaClassName,
          )}
          {...otherProps}
          {...restRHF}
          {...additionalProps}
          maxLength={maxLength}
          onFocus={() => {
            setIsFocused?.(true);
          }}
          onBlur={(event) => {
            onBlur && onBlur(event);
            onBlurRHF && onBlurRHF(event);
            setIsFocused?.(false);
          }}
          onChange={(event) => {
            onChangeRHF ? onChangeRHF(event) : otherProps?.onChange?.(event);
          }}
        />

        {helpTooltip && (
          <div className="absolute bottom-0 right-[10px] h-[34px] flex items-center">
            <HelpIconWithTooltip
              title={helpTooltip}
              tooltipPlacement={helpToolTipPlacement}
              strategy="fixed"
            />
          </div>
        )}

        {!!maxLength && showCharacterCount && (
          <CharactersCounter name={name} maxLength={maxLength} />
        )}
      </div>
      {!!isError && !!errorMessage && (
        <ErrorMessage>{errorMessage ?? ('' as ReactNode)}</ErrorMessage>
      )}
    </div>
  );
}

export const CharactersCounter = ({ name, maxLength }: { name: string; maxLength: number }) => {
  const { control } = useFormContext<FieldValues>();

  const value = useWatch({ name, control });
  return (
    <div
      className={`absolute bottom-2 right-4 flex gap-2 items-center text-sm text-v2blueGray-400 ${
        value?.length === maxLength ? '!text-red-500' : ''
      }`}
    >
      {value ? value?.length + `/${maxLength}` : '0' + `/${maxLength}`}
    </div>
  );
};

export type _TextAreaRhfProps<T> = {
  className?: string;
  textareaClassName?: string;
  name: Path<T>;
  showCharacterCount?: boolean;
  maxLength?: number;
  skipUnregisterOnUnmount?: boolean;
  autoResize?: boolean;
  maxRows?: number;
  minRows?: number;
  helpTooltip?: string;
  helpToolTipPlacement?: CustomTooltipProps['placement'];
};

export type TextAreaRhfProps<T extends FieldValues> = TextareaHTMLAttributes<HTMLTextAreaElement> &
  _TextAreaRhfProps<T>;

export function TextAreaRhf<T extends FieldValues>({
  name,
  skipUnregisterOnUnmount,
  showCharacterCount,
  maxLength,
  autoResize,
  maxRows,
  minRows,
  ...otherProps
}: TextAreaRhfProps<T>) {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const {
    register,
    unregister,
    getFieldState,
    control,
    formState: { errors },
  } = useFormContext<T>();

  useEffect(() => {
    return () => {
      if (skipUnregisterOnUnmount) {
        return;
      }
      const { isDirty } = getFieldState(name as Path<T>);
      unregister(name as Path<T>, {
        keepDirty: true,
        keepDefaultValue: true,
        keepValue: isDirty,
      });
    };
  }, [name, unregister, getFieldState]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, name: alias } }) => {
        const fieldError = get(errors, alias);

        return (
          <TextArea
            name={alias}
            register={register as (name: string) => void}
            isFocused={isFocused}
            setIsFocused={setIsFocused}
            isError={!!fieldError}
            errorMessage={fieldError?.message as string}
            maxLength={maxLength}
            showCharacterCount={showCharacterCount}
            value={value ?? ''}
            autoResize={autoResize}
            maxRows={maxRows}
            minRows={minRows}
            {...otherProps}
          />
        );
      }}
    />
  );
}

export default TextArea;
